import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useOnboarding } from '../../../components/redux/hooks/useOnboarding';
import { saveSelectedFeatures } from '../../../components/redux/slices/onboardingSlice';
import { usePostHog, POSTHOG_EVENTS } from '../../../components/analytics/PostHogProvider';
import api from '../../../services/api';

import SurveyForm from '../../../components/forms/SurveyForm';

const Welcome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const surveyRef = useRef(null);
  const [surveyJson, setSurveyJson] = useState(null);
  const { captureEvent } = usePostHog();
  const fetchCalledRef = useRef(false);
  
  const {
    features,
    isLoadingFeatures,
    featuresError,
    sessionId,
    isSavingFeatures,
    saveFeaturesError,
    featuresSaved,
    clearFeaturesErrorState,
    clearSaveFeaturesErrorState,
    setFeaturesLoadingState,
    setFeaturesDataState,
    setFeaturesErrorState,
  } = useOnboarding();
  
  // Transform API response to SurveyJS format
  const transformFeaturesToSurveyJson = (featuresData) => {
    if (!featuresData) {
      return null;
    }
    console.log(featuresData);
    // The API response is already in SurveyJS format, so return it directly
    return featuresData;
  };
  
  // Transform survey data to API format
  const transformSurveyDataToApiFormat = (surveyData, featuresList) => {
    if (!surveyData) {
      return {};
    }
    
    const features = {};
    
    // Extract elements from the API response structure
    const elements = featuresList?.pages?.[0]?.elements || [];
    
    // Map survey data back to API format using the element names
    elements.forEach(element => {
      const surveyKey = element.name;
      const isSelected = surveyData[surveyKey] && Array.isArray(surveyData[surveyKey]) && surveyData[surveyKey].length > 0;
      features[surveyKey] = isSelected;
    });
    
    return features;
  };
  
  // Fetch features function
  const fetchFeatures = useCallback(async () => {
    setFeaturesLoadingState(true);
    setFeaturesErrorState(null);
    try {
      const response = await api.get('/signup_landing_features/');
      setFeaturesDataState(response.data);
    } catch (error) {
      console.error('Failed to fetch features:', error);
      if (error.response && error.response.data) {
        setFeaturesErrorState(error.response.data);
      } else {
        setFeaturesErrorState({ error: 'Failed to get features. Please try again.' });
      }
    } finally {
      setFeaturesLoadingState(false);
    }
  }, [setFeaturesLoadingState, setFeaturesDataState, setFeaturesErrorState]);

  // Load features on component mount
  useEffect(() => {
    if (!fetchCalledRef.current) {
      fetchCalledRef.current = true;
      fetchFeatures();
    }
  }, [fetchFeatures]);
  
  // Transform features to SurveyJS format when they're loaded
  useEffect(() => {
    if (features) {
      const transformedJson = transformFeaturesToSurveyJson(features);
      setSurveyJson(transformedJson);
      captureEvent('features_survey_loaded', { 
        sessionId,
        featureCount: features?.pages?.[0]?.elements?.length || 0,
      });
    }
  }, [features, captureEvent, sessionId]);
  
  // Setup progress steps
  const setupSteps = [
    { id: 1, title: 'Basic Info', description: 'Clinic details and configuration', active: true },
    { id: 2, title: 'Connect EHR & Phone', description: 'Integration setup', active: false },
    { id: 3, title: 'Go-Live', description: 'Final review and activation', active: false },
  ];

  const handleStartSetup = () => {
    if (surveyRef.current) {
      surveyRef.current.completeLastPage();
    } else {
      // navigate('/onboarding/basic-info');
    }
  };

  const handleScheduleCall = () => {
    // Open scheduling modal or redirect to scheduling page
    console.log('Schedule onboarding call');
  };

  const handleSurveySubmit = async (data) => {
    console.log('Survey result:', data);
    console.log('Session ID:', sessionId);
    
    if (!sessionId) {
      console.error('No session ID available');
      return;
    }
    
    // Clear any previous save features errors
    clearSaveFeaturesErrorState();
    
    // Transform survey data to API format
    const apiFeatures = transformSurveyDataToApiFormat(data, features);
    
    try {
      // Dispatch the saveSelectedFeatures action
      const result = await dispatch(saveSelectedFeatures({
        sessionId,
        features: apiFeatures
      }));
      
      if (saveSelectedFeatures.fulfilled.match(result)) {
        console.log('Features saved successfully:', result.payload);
        captureEvent(POSTHOG_EVENTS.FEATURES_SAVED, { 
          sessionId,
          selectedFeatures: Object.keys(apiFeatures).filter(k => apiFeatures[k]),
        });
        
        // Open Odoo sign document popup
        // openSignPopup({
        //   documentId: '124',
        //   accessToken: '381c7c6d-d669-4e77-9388-e00032f2a790',
        //   metadata: { sessionId },
        //   onSuccess: (data) => {
        //     console.log('Document signed successfully:', data);
        //     captureEvent('contract_signed', { sessionId });
        //     navigate('/onboarding/dashboard');
        //   },
        //   onError: (err) => {
        //     console.error('Error signing document:', err);
        //     captureEvent('contract_sign_error', { sessionId, error: err });
        //     // Still navigate to dashboard on error - user can sign later
        //     navigate('/onboarding/dashboard');
        //   },
        //   onClose: () => {
        //     console.log('Sign popup closed without completion');
        //     captureEvent('contract_sign_closed', { sessionId });
        //     // Navigate to dashboard even if popup closed
            navigate('/onboarding/dashboard');
        //   }
        // });
      } else if (saveSelectedFeatures.rejected.match(result)) {
        console.error('Failed to save features:', result.payload);
        captureEvent('features_save_failed', { sessionId, error: result.payload });
        // Error is already in the Redux state, will be displayed in UI
      }
    } catch (error) {
      console.error('Unexpected error during features save:', error);
    }
  };

  return (
    <div className="mx-auto" style={{ maxWidth: 1100 }}>
      {/* Header Section */}
      <div className="mb-4">
        <h1 className="h4 fw-semibold text-dark mb-2">Welcome to Ankr — Let&apos;s get your clinic ready</h1>
        <p className="text-secondary" style={{ fontSize: '0.95rem' }}>
          You&apos;ll be able to stop/start — everything is saved. We&apos;ll provision systems (Twilio, VoiceFlow, Keycloak) as you complete steps.
        </p>
      </div>

      {/* Main Content */}
      <div className="row g-4 mb-4">
        {/* Left Panel - Setup Progress */}
        <div className="col-12 col-md-4">
          <div className="card border rounded-3 p-4 h-100">
            <h3 className="fs-6 fw-semibold text-dark mb-4">Setup Progress</h3>
            
            <div className="mb-4">
              {setupSteps.map((step, index) => (
                <div key={step.id} className="d-flex gap-3 mb-2">
                  <div className="d-flex flex-column align-items-center">
                    <div
                      className={`d-flex align-items-center justify-content-center rounded-circle fw-medium ${step.active ? 'bg-primary text-white' : 'bg-light text-secondary'}`}
                      style={{ width: 32, height: 32, fontSize: '0.875rem', border: step.active ? '2px solid #2563eb' : '2px solid #e2e8f0' }}
                    >
                      {step.id}
                    </div>
                    {index < setupSteps.length - 1 && (
                      <div style={{ width: 2, height: 40, backgroundColor: '#e2e8f0', margin: '4px 0' }}></div>
                    )}
                  </div>
                  <div>
                    <h4 className="mb-1 text-dark" style={{ fontSize: '0.9rem', fontWeight: 500 }}>{step.title}</h4>
                    <p className="text-secondary mb-0" style={{ fontSize: '0.8rem' }}>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-3 border-top">
              <div className="d-flex justify-content-between mb-2 small">
                <span className="text-secondary">Overall Progress</span>
                <span className="text-primary fw-medium">0%</span>
              </div>
              <div className="progress" style={{ height: 6 }}>
                <div className="progress-bar bg-primary" role="progressbar" style={{ width: '0%' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Onboarding Survey Form */}
        <div className="col-12 col-md-8">
          <div className="card border rounded-3 p-4 h-100">
            {/* <h3>Clinic Onboarding Survey</h3> */}
            <div className="mb-3">
              {isLoadingFeatures && (
                <div className="d-flex align-items-center justify-content-center py-4 text-secondary">
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mb-0">Loading onboarding features...</p>
                </div>
              )}
              
              {featuresError && (
                <div className="alert alert-danger d-flex align-items-center justify-content-between">
                  <p className="mb-0">Failed to load features. Please try again.</p>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => {
                    clearFeaturesErrorState();
                    fetchFeatures();
                  }}>
                    Retry
                  </button>
                </div>
              )}
              
              {!isLoadingFeatures && !featuresError && surveyJson && (
                <SurveyForm
                  json={surveyJson}
                  onSubmit={handleSurveySubmit}
                  surveyRef={surveyRef}
                />
              )}
              
              {!isLoadingFeatures && !featuresError && !surveyJson && (
                <div className="alert alert-info">
                  <p className="mb-0">No features available at this time.</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="d-flex justify-content-center gap-3 mb-3">
              <button 
                className="btn btn-primary fw-medium" 
                style={{ padding: '0.875rem 1.75rem', fontSize: '0.95rem' }}
                onClick={handleStartSetup} 
                disabled={!surveyJson || isSavingFeatures}
              >
                {isSavingFeatures ? 'Saving Features...' : 'Start Setup — Takes ~10 minutes'}
              </button>
              <button 
                className="btn btn-outline-secondary fw-medium" 
                style={{ padding: '0.875rem 1.75rem', fontSize: '0.95rem' }}
                onClick={handleScheduleCall}
              >
                Schedule Onboarding Call
              </button>
            </div>
            
            {/* Save Features Error */}
            {saveFeaturesError && (
              <div className="alert alert-danger d-flex align-items-center justify-content-between mt-2">
                <p className="mb-0">Failed to save features. Please try again.</p>
                <button className="btn btn-sm btn-outline-danger" onClick={() => {
                  clearSaveFeaturesErrorState();
                }}>
                  Dismiss
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="alert alert-info d-flex align-items-start gap-2 border">
        <i className="fas fa-info-circle text-primary mt-1"></i>
        <span className="small" style={{ lineHeight: 1.5, color: '#1e40af' }}>
          <strong>Estimated time: 10 minutes.</strong> Your progress is automatically saved, so you can complete this at your own pace. We&apos;ll begin provisioning your systems as you provide information.
        </span>
      </div>
    </div>
  );
};

export default Welcome;
